import { useEffect, useRef, useCallback } from 'react';

/**
 * Server-side infinite scroll hook.
 * Uses IntersectionObserver on a sentinel element to trigger `onLoadMore`.
 * The caller manages offset/page state externally.
 */
export function useServerInfiniteScroll(options: {
  /** Whether more pages are available */
  hasMore: boolean;
  /** Whether a fetch is in flight */
  isFetching: boolean;
  /** Callback to load the next page */
  onLoadMore: () => void;
}) {
  const { hasMore, isFetching, onLoadMore } = options;
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const stableLoadMore = useCallback(onLoadMore, [onLoadMore]);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore || isFetching) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          stableLoadMore();
        }
      },
      { rootMargin: '200px' }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, isFetching, stableLoadMore]);

  return { sentinelRef };
}
